import { useGetGcssMainpageQuery } from '@/api/sanity/generated';
import type { SimpleProduct } from '@/sections/gcss/ProductCard';
import type { Category } from './utils';
import { getAllPrimaryCategories } from './getCategories';
import type { EshopBox } from '@/components/boxes/EshopBox/types';
import { apiGcssFetcher } from '@/api/fetcher';
import { GCSS_URLS } from '@/constants/api';
import { MerchantsListResponse } from '@/api/skippay/gcssMerchants';
import { LINKS } from '@/constants/links';
import { FIELD_MERCHANT } from '../hooks/useGcssSearch';

export type MainpageData = {
	products: SimpleProduct[];
	categories: Category['subcategories'];
	eshops: EshopBox[];
};

export async function getMainpageData(): Promise<MainpageData> {
	const fetchAllPages = useGetGcssMainpageQuery.fetcher();

	const allPages = await fetchAllPages();

	// only one page should be returned
	if (!allPages || allPages.allGcssHomepage.length > 2) {
		throw new Error('Failed to fetch mainpage gcss data');
	}

	const data = allPages.allGcssHomepage[0];

	const products = (
		data.products?.map((product, index) => {
			if (!product) return null;

			return {
				brand: product.brand,
				id: `static-${index.toString()}`,
				imageLink: product.image?.asset?.url ?? null,
				merchantLink: product.link,
				priceAmount: product.price,
				priceCurrency: product.currency,
				originalPriceAmount: null,
				title: product.name,
			};
		}) || []
	).filter(Boolean) as SimpleProduct[];

	const primaryCategories = await getAllPrimaryCategories();

	const categories = (
		data.categories?.map((category) => {
			if (!category) return null;

			const mappedCategory = primaryCategories.find((c) => c.id === category.mappedCategory);

			if (!mappedCategory) return null;

			return {
				slug: [mappedCategory.slug],
				title: mappedCategory.name,
				image: category.image?.asset?.url ?? null,
			};
		}) || []
	).filter(Boolean) satisfies Category['subcategories'];

	const fetchAllMerchants = await apiGcssFetcher<Promise<MerchantsListResponse>>(
		GCSS_URLS.merchantsList
	);

	const merchants = await fetchAllMerchants.json;

	const eshops: EshopBox[] =
		data.eshops?.map((merchantName, index) => {
			const merchant = merchants?.find((m) => m.name === merchantName);

			if (!merchant) {
				throw new Error(`Merchant ${merchantName} not found`);
			}

			const link = new URL(process.env.NEXT_PUBLIC_BASE_URL + LINKS().gcssHomepageSearch);
			link.searchParams.set(FIELD_MERCHANT, merchant.name);

			return {
				id: merchant.name,
				// images from API are not available yet, so we use a fallback image
				image: {
					height: 0,
					size: 'contain',
					title: merchant.name,
					url: process.env.NEXT_PUBLIC_BASE_URL + '/static/img/skippay/eshop-placeholder.png',
					width: 0,
				},
				link: link.toString(),
				title: merchant.name,
				index,
				section: 'gcssHomepage',
				size: 'sm',
			};
		}) || [];

	return {
		categories,
		eshops,
		products,
	};
}
